'use client'

import { Globe } from 'lucide-react'
import { memo, type FC } from 'react'
import Flag from 'react-country-flag'

import { Tooltip, TooltipContent, TooltipTrigger } from '@fp/ui-kit'

// Lazy singleton — `Intl.DisplayNames` is non-trivial to allocate per call.
let countryDisplayNames: Intl.DisplayNames | null = null

export const getCountryName = (code: string): string | undefined => {
  if (!code) return undefined
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined') return undefined
  if (!countryDisplayNames) countryDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' })
  try {
    return countryDisplayNames.of(code.toUpperCase())
  } catch {
    return undefined
  }
}

interface FlagCountryProps {
  countryCode?: string | null
}

/**
 * Country flag + ISO code with a tooltip showing the full localized country
 * name. Renders the bare content (no tooltip) when the name can't be resolved.
 */
export const FlagCountry: FC<FlagCountryProps> = memo(({ countryCode = '' }) => {
  const fullName = getCountryName(countryCode || '')
  const isKnown = Boolean(fullName)

  const content = (
    <div className='flex flex-row items-center gap-2'>
      <div className='relative aspect-square mr-1 h-4! w-4 rounded-full overflow-hidden'>
        {isKnown ? (
          <Flag
            className='absolute inset-0 object-cover'
            svg
            alt='country flag'
            countryCode={countryCode!}
            style={{ width: '100%', height: '100%' }}
          />
        ) : (
          <Globe className='absolute inset-0 h-full w-full text-muted-foreground' aria-hidden />
        )}
      </div>
      {countryCode || '—'}
    </div>
  )

  if (!isKnown) return content

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side='top'>{fullName}</TooltipContent>
    </Tooltip>
  )
})

FlagCountry.displayName = 'FlagCountry'
